import React, { useEffect, useCallback } from 'react';
import styles from './ConteudoAudio.module.css';
import WaveSurfer from 'wavesurfer.js';

import Botao from '../../../components/Botao';
import {
  mdiInformationBox,
  mdiMicrophoneMessage,
  mdiPause,
  mdiPlay,
} from '@mdi/js';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import { ChatMensagemDto } from '@tera/shared/src/dto/ChatMensagemDto';
import Icon from '@/components/Icon';
import { Tooltip } from '@/components/layout/Tooltip';

type ConteudoAudioProps = {
  mime: string;
  value: ChatMensagemDto;
  onAudioPlaying?: (isPlaying: boolean) => void;
};

const ConteudoAudio = ({ mime, value, onAudioPlaying }: ConteudoAudioProps) => {
  const waveformRef = React.useRef<HTMLDivElement>(null);
  const wavesurfer = React.useRef<WaveSurfer | null>(null);
  const audioElementRef = React.useRef<HTMLAudioElement | null>(null);

  const [isPlaying, setIsPlaying] = React.useState<boolean>(false);
  const [isReady, setIsReady] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    onAudioPlaying?.(isPlaying);
  }, [isPlaying]);

  const handleClick = useCallback(async () => {
    try {
      if (!wavesurfer.current) return;

      if (wavesurfer.current.isPlaying()) {
        wavesurfer.current.pause();
      } else {
        // Garantir que o áudio não está mutado e volume está alto
        const mediaElement = wavesurfer.current.getMediaElement();
        if (mediaElement) {
          mediaElement.volume = 1;
          mediaElement.muted = false;
          console.log('Audio element state before play:', {
            volume: mediaElement.volume,
            muted: mediaElement.muted,
            paused: mediaElement.paused,
            readyState: mediaElement.readyState,
            src: mediaElement.src
          });
        }

        try {
          await wavesurfer.current.play();
          console.log('WaveSurfer play initiated');
        } catch (err) {
          console.warn('Erro ao tocar áudio via WaveSurfer:', err);
          // Fallback: tenta tocar diretamente via elemento de áudio
          if (mediaElement) {
            console.log('Tentando fallback com elemento de áudio nativo...');
            await mediaElement.play();
          }
        }
      }
    } catch (err) {
      console.warn('Error on play/pause', err);
    }
  }, []);

  React.useEffect(() => {
    if (waveformRef.current) {
      const audioUrl = `/api/assets${value.conteudo_ou_url}`;

      wavesurfer.current = WaveSurfer.create({
        container: waveformRef.current,
        url: audioUrl,
        dragToSeek: true,
        normalize: true,
        waveColor: '#6f8090',
        progressColor: '#2f8719',
        cursorColor: '#2f8719',
        barWidth: 2,
        barGap: 4,
        barRadius: 2,
        height: 40,
        cursorWidth: 16,
      });

      // Guardar referência ao elemento de áudio interno do WaveSurfer
      audioElementRef.current = wavesurfer.current.getMediaElement();

      wavesurfer.current.on('ready', () => {
        setIsReady(true);
        setError(null);
        console.log('WaveSurfer ready, duration:', wavesurfer.current?.getDuration());
      });

      wavesurfer.current.on('play', () => {
        setIsPlaying(true);
      });

      wavesurfer.current.on('pause', () => {
        setIsPlaying(false);
      });

      wavesurfer.current.on('finish', () => {
        setIsPlaying(false);
      });

      wavesurfer.current.on('destroy', () => {
        setIsPlaying(false);
      });

      wavesurfer.current.on('error', (err) => {
        console.error('WaveSurfer error:', err);
        setError('Erro ao carregar áudio');
      });

    }

    const wavesurferInstance = wavesurfer.current;

    return () => {
      wavesurferInstance?.destroy();
    };
  }, [value]);

  return (
    <div className={styles.container}>
      <VBox>
        {value?.metadados?.duracao && (
          <HBox style={{ fontSize: '12px', color: 'var(--tc-color-gray-500)', justifyContent: 'right' }}>
            {Math.floor(value.metadados.duracao / 60)
              .toString()
              .padStart(2, '0')}
            :
            {(Math.floor(value.metadados.duracao % 60))
              .toString()
              .padStart(2, '0')}
          </HBox>
        )}
        <HBox>
          <div className={styles.controls}>
            <Botao
              variant="none-secondary"
              icon={isPlaying ? mdiPause : mdiPlay}
              onClick={handleClick}
              disabled={!!error}
            />
          </div>
          <div ref={waveformRef} className={styles.waveform}></div>
        </HBox>
        {error && (
          <HBox style={{ color: 'var(--tc-color-red-500)', fontSize: '12px' }}>
            <span>{error}</span>
          </HBox>
        )}
        {value?.metadados?.transcricao && (
          <HBox
            stretch
            style={{ color: 'var(--tc-color-gray-500)', fontSize: '14px' }}
          >
            <Icon
              path={mdiMicrophoneMessage}
              size={0.8}
              color="var(--tc-color-gray-400)"
            />
            <span className='rr-mask'>{value.metadados.transcricao}</span>
            <Tooltip
              position="top"
              text="A transcrição do áudio foi gerada automaticamente e pode não ser exata."
            >
              <Icon
                path={mdiInformationBox}
                size={0.8}
                color="var(--tc-color-gray-400)"
              />
            </Tooltip>
          </HBox>
        )}
      </VBox>
    </div >
  );
};

export default ConteudoAudio;
